/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Enforce the portal-fixed audience and compose the workspace chrome.
 * @state-owner authenticated audience context, local stampless-session recovery state, optional organization query, and the navigation store
 * @allowed-imports React; React Router; app providers; auth module public API; settings-preferences
 *   module public API; shared components; shell parts
 * @forbidden in-product role switching; direct API client; module internals
 * @public-api app router
 * @side-effects navigation, authenticated sign-out recovery, CSS custom property updates, a one-shot
 *   language hydration from the backend-authoritative preferences, and transient UI feedback
 * @decision The student or teacher portal fixes the role for the session. A cross-audience URL
 *   redirects to the established portal audience and never renders an in-product role chooser.
 * @decision Interface-language hydration applies the backend preference exactly once per mount —
 *   see use-language-hydration.ts for the guard's rationale and its dedicated unit tests.
 * @tests app/router/routes.test.tsx, use-language-hydration.test.ts, and workspace Playwright
 *   flows in frontend/apps/web/e2e/workspace.spec.ts and e2e/sh-f02.spec.ts
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useWorkspaceEnvironment } from '../app/config/environment';
import { ActiveRoleProvider } from '../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../app/providers/SessionProvider';
import { WorkspaceDataHydrator } from '../app/providers/WorkspaceDataHydrator';
import { TutorThread } from '../modules/activities';
import { useActiveContext, useSelectActiveContext } from '../modules/auth';
import { usePreferences } from '../modules/settings-preferences';
import { VispoAnimatedMark } from '../shared/components/BrandMarks';
import { OverlayHost } from '../shared/components/OverlayHost';
import type { WorkspaceRole, WorkspaceSection } from '../shared/lib/model';
import { defaultSelection } from '../shared/lib/selectors';
import { useDatasetStore } from '../shared/model/dataset-store';
import { useIsMobile } from '../shared/model/use-workspace';
import { PANEL_CSS_VARIABLE, useWorkspaceUiStore } from '../shared/model/workspace-store';
import { CommandPalette } from './shell/CommandPalette';
import { ListColumn } from './shell/ListColumn';
import { MainColumn } from './shell/MainColumn';
import { MobileWorkspaceShell } from './shell/MobileWorkspaceShell';
import { SearchOverlay } from './shell/SearchOverlay';
import { normalizeSettingsTab, SettingsShell, type SettingsTab } from './shell/SettingsShell';
import { ShellKeyboard } from './shell/ShellKeyboard';
import { StudentSettingsShell } from './shell/StudentSettingsShell';
import { Toast } from './shell/Toast';
import { TopBar } from './shell/TopBar';
import { WorkspaceRail } from './shell/WorkspaceRail';
import { useLanguageHydration } from './use-language-hydration';

const PATH_SECTION: Readonly<Record<string, WorkspaceSection>> = {
  '/activities': 'act',
  '/classes': 'room',
  '/live': 'live',
  '/reports': 'rep',
};

const SECTION_PATH: Readonly<Record<WorkspaceSection, string>> = {
  act: '/teacher/activities',
  room: '/teacher/classes',
  live: '/teacher/live',
  rep: '/teacher/reports',
};

const MIN_SETTINGS_LOADING_VISIBLE_MS = 160;

export function WorkspaceShell({ desiredRole }: { desiredRole: WorkspaceRole }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { demoMode, enforcePortalAudience } = useWorkspaceEnvironment();
  const sessionState = useWorkspaceSession();
  const activeContextQuery = useActiveContext();
  const selectActiveContext = useSelectActiveContext();
  const preferencesQuery = usePreferences();
  useLanguageHydration(preferencesQuery.data);
  const role = activeContextQuery.data?.role ?? null;
  const selectedOrganizationId =
    new URLSearchParams(location.search).get('organizationId')?.trim() || null;
  const settingsSegment = location.pathname.split('/')[3];
  const teacherSettingsPath =
    desiredRole === 'teacher' && location.pathname.startsWith('/teacher/settings');
  const studentSettingsPath =
    desiredRole === 'student' && location.pathname.startsWith('/student/settings');
  const mobile = useIsMobile();
  const backFromStudentSettings = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/student/workspace', { replace: true });
  };
  const personalSelectionIntent =
    typeof location.state === 'object' &&
    location.state !== null &&
    'workspaceSelection' in location.state &&
    location.state.workspaceSelection === 'personal';
  const completePersonalRebase = () => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  };

  const personalRebaseRequired =
    (sessionState.session?.portalAudience ?? desiredRole) === 'teacher' &&
    !selectedOrganizationId &&
    personalSelectionIntent;

  if (sessionState.isLoading) return <WorkspaceGate title="正在確認登入狀態…" />;

  if (sessionState.error) {
    return (
      <WorkspaceGate
        action={{ label: '重試', onClick: () => void sessionState.refresh() }}
        description={sessionState.error.message}
        title="無法確認登入狀態"
      />
    );
  }

  if (!sessionState.session) return <Navigate replace to="/login" />;

  const portalAudience = sessionState.session.portalAudience;
  if (!portalAudience && (!demoMode || enforcePortalAudience)) return <StamplessSessionGate />;
  if (portalAudience && portalAudience !== desiredRole) {
    return <Navigate replace to={`/${portalAudience}/workspace`} />;
  }

  if (activeContextQuery.isLoading) return <WorkspaceGate title="正在載入你的工作區…" />;

  if (activeContextQuery.isError) {
    return (
      <WorkspaceGate
        action={{ label: '重試', onClick: () => void activeContextQuery.refetch() }}
        description="登入仍然有效，請重新載入由 Vispo API 管理的身分內容。"
        title="工作區暫時無法載入"
      />
    );
  }

  if (
    !role ||
    (portalAudience && role !== portalAudience) ||
    (selectedOrganizationId &&
      activeContextQuery.data?.organizationId !== selectedOrganizationId) ||
    personalRebaseRequired
  ) {
    return (
      <WorkspaceRoleBootstrap
        desiredRole={portalAudience ?? desiredRole}
        organizationId={selectedOrganizationId}
        onSuccess={personalRebaseRequired ? completePersonalRebase : undefined}
        select={selectActiveContext}
      />
    );
  }

  return (
    <ActiveRoleProvider
      contextId={activeContextQuery.data?.id ?? 'inactive'}
      organizationId={activeContextQuery.data?.organizationId ?? null}
      role={role}
    >
      <OverlayHost>
        {teacherSettingsPath ? (
          <>
            {/* AST-F01/SET-F09: the AI Assistant settings tab renders from the dataset store, not
                its own query result directly -- without this hydrator mounted here too, a direct
                or reloaded navigation into Settings leaves that store on its module-load defaults
                (the demo assistant roster) instead of this organization's real, connected data. */}
            <WorkspaceDataHydrator
              activeContextId={activeContextQuery.data?.id ?? 'inactive'}
              organizationId={activeContextQuery.data?.organizationId ?? null}
              workspaceRole={role}
            />
            <TeacherSettingsSurface segment={settingsSegment} />
          </>
        ) : studentSettingsPath ? (
          <StudentSettingsShell
            onBack={backFromStudentSettings}
            surface={mobile ? 'mobile' : 'desktop'}
          />
        ) : (
          <>
            <WorkspaceDataHydrator
              activeContextId={activeContextQuery.data?.id ?? 'inactive'}
              organizationId={activeContextQuery.data?.organizationId ?? null}
              workspaceRole={role}
            />
            <WorkspaceChrome entryPath={location.pathname} role={role} />
          </>
        )}
      </OverlayHost>
    </ActiveRoleProvider>
  );
}

function TeacherSettingsSurface({ segment }: { segment?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = useIsMobile();
  const tab = normalizeSettingsTab(segment);
  const [loadedTab, setLoadedTab] = useState<SettingsTab | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (segment === tab) return;
    navigate(`/teacher/settings/${tab}`, { replace: true });
  }, [navigate, segment, tab]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `location.key` changes on every navigation, including a re-selection of the already-active tab, so the loading state is gated on the observed navigation event rather than on `tab` value equality alone.
  useEffect(() => {
    setLoadedTab(null);
    let frame = 0;
    let paintedAt: number | null = null;
    // Anchor the minimum-visible window to the timestamp of the first frame that
    // actually painted the loading state, instead of a bare setTimeout deadline. A
    // setTimeout can expire and flip state before the browser ever paints an
    // intermediate frame (e.g. inside a delayed CPU-contended task burst), which is
    // what made the loading assertion flaky. Gating on an observed paint means the
    // state is guaranteed to have rendered before the minimum window is measured.
    const tick = (timestamp: number) => {
      if (paintedAt === null) paintedAt = timestamp;
      if (timestamp - paintedAt >= MIN_SETTINGS_LOADING_VISIBLE_MS) {
        setLoadedTab(tab);
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [tab, location.key]);

  const select = (next: SettingsTab) => navigate(`/teacher/settings/${next}`);
  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/teacher/workspace', { replace: true });
  };
  const goToWorkspaceSection = (section: WorkspaceSection) => navigate(SECTION_PATH[section]);

  if (mobile) {
    return (
      <SettingsShell
        loading={loadedTab !== tab}
        onBack={back}
        onSelect={select}
        surface="mobile"
        tab={tab}
      />
    );
  }

  // The chrome is invariant: every desktop route renders the same top bar and rail,
  // and only the framed content changes. Settings renders from the URL, not the workspace
  // store, so the shared top bar's history/search/recent controls are given navigation-aware
  // overrides here instead of the plain store mutations WorkspaceChrome uses.
  return (
    <>
      <TopBar
        navigation={{
          onBack: back,
          onSelectSnapshot: (snapshot) => goToWorkspaceSection(snapshot.section),
        }}
        onOpenSearch={() => setSearchOpen(true)}
      />
      <div className="app">
        <WorkspaceRail onNavigate={goToWorkspaceSection} />
        <SettingsShell loading={loadedTab !== tab} onSelect={select} surface="desktop" tab={tab} />
      </div>
      <SearchOverlay
        onClose={() => setSearchOpen(false)}
        onLeaveToSection={goToWorkspaceSection}
        open={searchOpen}
      />
    </>
  );
}

function StamplessSessionGate() {
  const navigate = useNavigate();
  const { signOut } = useWorkspaceSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restart = async () => {
    setBusy(true);
    setError(null);
    try {
      await signOut();
      await navigate('/login', { replace: true });
    } catch {
      setError('無法結束目前登入，請再試一次。');
      setBusy(false);
    }
  };

  return (
    <WorkspaceGate
      action={{ label: busy ? '正在登出…' : '登出並重新登入', onClick: () => void restart() }}
      description={error ?? '為保護你的工作區，請從原本的登入入口重新登入。'}
      title="請重新登入"
    />
  );
}

function WorkspaceRoleBootstrap({
  desiredRole,
  organizationId,
  onSuccess,
  select,
}: {
  desiredRole: WorkspaceRole;
  organizationId: string | null;
  onSuccess?: () => void;
  select: ReturnType<typeof useSelectActiveContext>;
}) {
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void select.mutateAsync({ organizationId, role: desiredRole }).then(onSuccess);
  }, [desiredRole, onSuccess, organizationId, select]);

  if (select.isError) {
    return (
      <WorkspaceGate
        action={{
          label: '重試',
          onClick: () => {
            started.current = true;
            void select.mutateAsync({ organizationId, role: desiredRole }).then(onSuccess);
          },
        }}
        description={select.error.message}
        title="無法進入工作區"
      />
    );
  }

  return (
    <WorkspaceGate
      title={desiredRole === 'teacher' ? '正在準備老師工作區…' : '正在準備學生工作區…'}
    />
  );
}

function WorkspaceChrome({ entryPath, role }: { entryPath: string; role: WorkspaceRole }) {
  const mobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const seededRef = useRef(false);
  const panelWidths = useWorkspaceUiStore((state) => state.panelWidths);
  const go = useWorkspaceUiStore((state) => state.go);
  const consumePendingSelection = useWorkspaceUiStore((state) => state.consumePendingSelection);
  const classes = useDatasetStore((state) => state.classes);
  const runs = useDatasetStore((state) => state.runs);

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    const section =
      Object.entries(PATH_SECTION).find(([path]) => entryPath.endsWith(path))?.[1] ?? 'room';
    // A search result picked from a route that had to leave first (e.g. Settings) queues its
    // selection in the store before this component ever mounts; yield to it here instead of
    // unconditionally overwriting it with this section's own default (SH-F02 round-3 rework,
    // Sol BLOCKING finding 3 -- the prior write alone never survived this real mount).
    const pending = consumePendingSelection(section);
    go(section, pending ?? defaultSelection(section, role, classes, runs));
  }, [classes, consumePendingSelection, entryPath, go, role, runs]);

  useEffect(() => {
    for (const [key, variable] of Object.entries(PANEL_CSS_VARIABLE)) {
      const width = panelWidths[key as keyof typeof panelWidths];
      document.documentElement.style.setProperty(variable, `${width}px`);
    }
  }, [panelWidths]);

  const navigateSection = (section: WorkspaceSection) =>
    go(section, defaultSelection(section, role, classes, runs));

  if (mobile) {
    return (
      <>
        <MobileWorkspaceShell
          onNavigate={navigateSection}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <TutorThread />
        <SearchOverlay onClose={() => setSearchOpen(false)} open={searchOpen} />
        <Toast />
      </>
    );
  }

  return (
    <>
      <TopBar onOpenSearch={() => setSearchOpen(true)} />
      <div className="app">
        <WorkspaceRail onNavigate={navigateSection} />
        <div className="shellcard">
          <ListColumn />
          <MainColumn />
          <TutorThread />
        </div>
      </div>
      <SearchOverlay onClose={() => setSearchOpen(false)} open={searchOpen} />
      <CommandPalette
        onClose={() => setPaletteOpen(false)}
        onNavigateSection={navigateSection}
        open={paletteOpen}
      />
      <ShellKeyboard
        onCloseSearch={() => setSearchOpen(false)}
        onNavigateSection={navigateSection}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <Toast />
    </>
  );
}

function WorkspaceGate({
  action,
  description,
  title,
}: {
  action?: { label: string; onClick(): void };
  description?: string;
  title: string;
}) {
  return (
    <main className="workspace-gate">
      <VispoAnimatedMark className="jlogo" />
      <strong>{title}</strong>
      {description ? <p>{description}</p> : null}
      {action ? (
        <button className="bt pri" type="button" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </main>
  );
}
